const OPENPAY_JS = 'https://js.openpay.mx/openpay.v1.min.js';
const OPENPAY_DATA_JS = 'https://js.openpay.mx/openpay-data.v1.min.js';

export interface OpenPayToken {
  id: string;
  card?: {
    brand?: string;
    card_number?: string;
  };
}

export interface OpenPayError {
  message?: string;
  description?: string;
  error_code?: number;
  category?: string;
}

export interface OpenPayCardParams {
  card_number: string;
  holder_name: string;
  expiration_year: string;
  expiration_month: string;
  cvv2: string;
}

export interface OpenPayStatic {
  setId: (id: string) => void;
  setApiKey: (key: string) => void;
  setSandboxMode: (flag: boolean) => void;
  token: {
    create: (
      params: OpenPayCardParams | { card: OpenPayCardParams },
      success: (response: { data: OpenPayToken }) => void,
      error: (response: { data: OpenPayError; message?: string }) => void,
    ) => void;
  };
  deviceData: {
    setup: (formId?: string, hiddenFieldName?: string) => string;
  };
  card: {
    validateCardNumber: (n: string) => boolean;
    validateCVC: (cvc: string, number?: string) => boolean;
    validateExpiry: (month: string, year: string) => boolean;
    cardType: (n: string) => string;
  };
}

declare global {
  interface Window {
    OpenPay?: OpenPayStatic;
  }
}

function injectScript(src: string): void {
  if (document.querySelector(`script[src="${src}"]`)) return;
  const script = document.createElement('script');
  script.src = src;
  script.async = false;
  document.head.appendChild(script);
}

function waitForOpenPay(timeoutMs = 8000): Promise<OpenPayStatic> {
  return new Promise((resolve, reject) => {
    const started = Date.now();
    const tick = () => {
      const OpenPay = window.OpenPay;
      if (OpenPay) {
        resolve(OpenPay);
        return;
      }
      if (Date.now() - started > timeoutMs) {
        reject(
          new Error(
            'OpenPay.js no cargó. Recargue con Ctrl+Shift+R o desactive el bloqueador.',
          ),
        );
        return;
      }
      window.setTimeout(tick, 50);
    };
    tick();
  });
}

export async function loadOpenPay(
  merchantId: string,
  publicKey: string,
  sandbox: boolean,
): Promise<OpenPayStatic> {
  if (!window.OpenPay) {
    injectScript(OPENPAY_JS);
    injectScript(OPENPAY_DATA_JS);
  }

  const OpenPay = await waitForOpenPay();
  OpenPay.setId(merchantId);
  OpenPay.setApiKey(publicKey);
  OpenPay.setSandboxMode(!!sandbox);
  return OpenPay;
}

export function fallbackDeviceSession(): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let id = '';
  for (let i = 0; i < 32; i += 1) {
    id += chars[Math.floor(Math.random() * chars.length)];
  }
  return id;
}

export function setupDeviceSession(OpenPay: OpenPayStatic): string {
  try {
    const id = OpenPay.deviceData?.setup?.();
    if (id) return String(id);
  } catch {
    /* sandbox a veces no entrega beacon de Sift */
  }
  return fallbackDeviceSession();
}

export function tokenizeCard(
  OpenPay: OpenPayStatic,
  card: OpenPayCardParams,
): Promise<string> {
  return new Promise((resolve, reject) => {
    OpenPay.token.create(
      card,
      (response) => {
        const id = response?.data?.id;
        if (!id) {
          reject(new Error('No se pudo validar la tarjeta. Intente de nuevo.'));
          return;
        }
        resolve(id);
      },
      (response) => {
        reject(
          new Error(
            mensajeRechazoOpenpay(response?.data?.error_code, {
              description: response?.data?.description,
              message: response?.data?.message || response?.message,
            }),
          ),
        );
      },
    );
  });
}

export function digitsOnly(value: string): string {
  return value.replace(/\D+/g, '');
}

export function formatCardNumber(value: string): string {
  return digitsOnly(value)
    .slice(0, 19)
    .replace(/(.{4})/g, '$1 ')
    .trim();
}

export function browserHttpContext(): Record<string, string | boolean> {
  return {
    browser: navigator.userAgent,
    domain: window.location.hostname,
    browser_screen_height: String(window.screen.height),
    browser_screen_width: String(window.screen.width),
    browser_user_agent: navigator.userAgent,
    browser_language: navigator.language || 'es-MX',
    browser_java_enabled: false,
    browser_javascript_enabled: true,
  };
}

export function etiquetaMarcaTarjeta(marca: string): string {
  switch ((marca || '').toLowerCase()) {
    case 'visa':
      return 'Visa';
    case 'mastercard':
      return 'Mastercard';
    case 'american_express':
      return 'American Express';
    case 'carnet':
      return 'Carnet';
    default:
      return marca || 'Tarjeta';
  }
}

/** Mensajes claros para el cliente cuando el banco o Openpay rechazan el cargo. */
export function mensajeRechazoOpenpay(
  code?: number | string | null,
  extra?: { description?: string; message?: string },
): string {
  const n = Number(code) || 0;
  const map: Record<number, string> = {
    1000: 'El servicio de pago no respondió. Espere un momento e intente de nuevo.',
    1001: 'Los datos del pago no son válidos. Revise la tarjeta y la dirección.',
    1002: 'No se pudo autenticar el pago. Recargue la página e intente de nuevo.',
    1003: 'Esta operación no está permitida. Use otra tarjeta o llámenos.',
    1004: 'No se encontró el pago. Intente de nuevo desde el checkout.',
    1005: 'La solicitud tardó demasiado. Intente de nuevo.',
    2004: 'Esta tarjeta ya está registrada. Elíjala de su lista o use otra.',
    2005: 'La fecha de vencimiento no es válida o la tarjeta ya venció.',
    2006: 'Falta el código de seguridad (CVV) de la tarjeta.',
    2007: 'Falta el número de tarjeta.',
    2008: 'El número de tarjeta no es válido. Verifíquelo.',
    2009: 'El código CVV no es válido.',
    2010: 'La fecha de vencimiento no es válida.',
    2011: 'El tipo de tarjeta no es compatible. Use Visa o Mastercard.',
    3001: 'El banco rechazó el cargo. Verifique los datos, use otra tarjeta o pida autorización a su banco.',
    3002: 'La tarjeta está vencida. Use otra.',
    3003: 'La tarjeta no tiene fondos suficientes.',
    3004: 'El banco rechazó esta tarjeta. Contacte a su banco o use otra.',
    3005: 'El banco rechazó el pago por seguridad. Contacte a su banco o use otra tarjeta.',
    3006: 'Esta operación no está permitida para la tarjeta. Use otra o contacte a su banco.',
    3007: 'El banco rechazó el cargo. Intente con otra tarjeta.',
    3008: 'Esta tarjeta no admite pagos en línea. Use otra Visa o Mastercard.',
    3009: 'El banco reportó la tarjeta. Contacte a su banco o use otra.',
    3010: 'El banco tiene restringida la tarjeta. Contacte a su banco.',
    3011: 'Su banco pide autorización para este pago. Llámele o use otra tarjeta.',
    3012: 'Se requiere autorización del banco. Contacte a su banco e intente de nuevo.',
  };
  if (n && map[n]) return map[n];

  const raw = `${extra?.description || ''} ${extra?.message || ''}`.toLowerCase();
  if (raw.includes('declin') || raw.includes('denied') || raw.includes('reject')) {
    return map[3001];
  }
  if (raw.includes('expir') || raw.includes('vencid')) return map[3002];
  if (raw.includes('insufficient') || raw.includes('fondos')) return map[3003];
  if (raw.includes('stolen') || raw.includes('fraud') || raw.includes('lost')) return map[3005];
  if (raw.includes('cvv') || raw.includes('cvc') || raw.includes('security code')) return map[2009];

  return 'No se pudo completar el pago. Verifique los datos de la tarjeta o use otra. Si continúa, llámenos al 614 440 0040.';
}

export function textoErrorPago(raw: string): string {
  const t = (raw || '').trim();
  if (!t) return mensajeRechazoOpenpay();
  const codeMatch = t.match(/\b([123]\d{3})\b/);
  if (codeMatch) return mensajeRechazoOpenpay(codeMatch[1], { message: t });
  if (/^(el |la |los |esta |este |no |falta |su |verifique |intente |complete |indique |openpay aún)/i.test(t)) {
    return t;
  }
  return mensajeRechazoOpenpay(undefined, { message: t });
}
